import * as React from "react";
import { useMutation, useQuery } from "react-query";
import {
  ApiError,
  AiPrompts,
  AiPromptsOverrides,
  ClientConfig,
  ClientsService,
  SystemSettingsService,
  UserGroup
} from "gen/clients";
import { PageLoading } from "components/PageLoading/PageLoading";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { useDialogState } from "hooks/useDialogState";
import { ClientAiPromptsTable } from "./components/ClientAiPromptsTable";
import { EditClientAiPromptDialog } from "./components/EditClientAiPromptDialog";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { SnackbarAlert } from "components/SnackbarAlert/SnackbarAlert";
import { useUserSession } from "hooks/useUserSession";
import { useClientReadOnly } from "contexts/ClientLockContext";

export interface ClientPromptItem {
  key: keyof AiPrompts;
  name: string;
  description: string;
  defaultValue: string;
  overrideValue?: string;
  isOverridden: boolean;
}

interface Props {
  clientConfig: ClientConfig;
  onSaveSuccess: () => void;
}

const AiPromptsTab: React.FC<Props> = ({ clientConfig, onSaveSuccess }) => {
  const isReadOnly = useClientReadOnly();
  const [editDialogOpen, openEditDialog, closeEditDialog] = useDialogState();
  const [selectedPrompt, setSelectedPrompt] = React.useState<ClientPromptItem | null>(null);
  const { roles } = useUserSession();

  const isSuperUser = React.useMemo(() => roles?.includes(UserGroup.SUPER_USER) || false, [roles]);

  const {
    data: systemAiPrompts,
    isLoading: isSystemPromptsLoading,
    error: systemPromptsError
  } = useQuery<AiPrompts, ApiError>(["getAiPrompts"], {
    queryFn: SystemSettingsService.getAiPrompts
  });

  const {
    mutateAsync: updateClientConfig,
    isLoading: isUpdating,
    error: updateError,
    isSuccess: updateSuccess
  } = useMutation<void, ApiError, { id: string; requestBody: ClientConfig }>(ClientsService.updateClientConfig);

  const promptItems: ClientPromptItem[] = React.useMemo(() => {
    if (!systemAiPrompts) {
      return [];
    }

    const overrides = clientConfig.aiPromptsOverrides || {};

    return [
      {
        key: "linguisticQualityReviewPrompt",
        name: "Text Linguistic Quality Review",
        description: "Prompt for AI linguistic quality review of text",
        defaultValue: systemAiPrompts.linguisticQualityReviewPrompt,
        overrideValue: overrides.linguisticQualityReviewPrompt,
        isOverridden: overrides.linguisticQualityReviewPrompt !== undefined
      },
      {
        key: "pdfLinguisticQualityReviewPrompt",
        name: "PDF Linguistic Quality Review",
        description: "Prompt for AI linguistic quality review of PDF documents (PDF comparison)",
        defaultValue: systemAiPrompts.pdfLinguisticQualityReviewPrompt,
        overrideValue: overrides.pdfLinguisticQualityReviewPrompt,
        isOverridden: overrides.pdfLinguisticQualityReviewPrompt !== undefined
      },
      {
        key: "xliffLinguisticQualityReviewPrompt",
        name: "XLIFF Linguistic Quality Review",
        description: "Prompt for AI linguistic quality review of XLIFF documents (source vs target comparison)",
        defaultValue: systemAiPrompts.xliffLinguisticQualityReviewPrompt,
        overrideValue: overrides.xliffLinguisticQualityReviewPrompt,
        isOverridden: overrides.xliffLinguisticQualityReviewPrompt !== undefined
      },
      {
        key: "linguisticQualityReviewsSummaryPrompt",
        name: "Linguistic Quality Review Summary",
        description: "Prompt for AI to summarize multiple linguistic quality reviews",
        defaultValue: systemAiPrompts.linguisticQualityReviewsSummaryPrompt,
        overrideValue: overrides.linguisticQualityReviewsSummaryPrompt,
        isOverridden: overrides.linguisticQualityReviewsSummaryPrompt !== undefined
      },
      {
        key: "postEditingPrompt",
        name: "Text Post Editing",
        description: "Prompt for AI post editing of machine translated text",
        defaultValue: systemAiPrompts.postEditingPrompt,
        overrideValue: overrides.postEditingPrompt,
        isOverridden: overrides.postEditingPrompt !== undefined
      },
      {
        key: "filePostEditingPrompt",
        name: "File Post Editing",
        description: "Prompt for AI file post editing",
        defaultValue: systemAiPrompts.filePostEditingPrompt,
        overrideValue: overrides.filePostEditingPrompt,
        isOverridden: overrides.filePostEditingPrompt !== undefined
      }
    ];
  }, [systemAiPrompts, clientConfig.aiPromptsOverrides]);

  const handleEditClick = React.useCallback(
    (prompt: ClientPromptItem) => {
      setSelectedPrompt(prompt);
      openEditDialog();
    },
    [openEditDialog]
  );

  const handleSavePrompt = React.useCallback(
    async (updatedValue: string) => {
      if (!selectedPrompt) {
        return;
      }

      const updatedOverrides: AiPromptsOverrides = {
        ...clientConfig.aiPromptsOverrides,
        [selectedPrompt.key]: updatedValue
      };

      await updateClientConfig({
        id: clientConfig.id,
        requestBody: {
          ...clientConfig,
          aiPromptsOverrides: updatedOverrides
        }
      });

      onSaveSuccess();
      closeEditDialog();
    },
    [selectedPrompt, clientConfig, updateClientConfig, onSaveSuccess, closeEditDialog]
  );

  const handleResetPrompt = React.useCallback(
    async (prompt: ClientPromptItem) => {
      const updatedOverrides: AiPromptsOverrides = { ...clientConfig.aiPromptsOverrides };
      delete updatedOverrides[prompt.key];

      await updateClientConfig({
        id: clientConfig.id,
        requestBody: {
          ...clientConfig,
          aiPromptsOverrides: Object.keys(updatedOverrides).length > 0 ? updatedOverrides : undefined
        }
      });

      onSaveSuccess();
    },
    [clientConfig, updateClientConfig, onSaveSuccess]
  );

  if (isSystemPromptsLoading) {
    return <PageLoading />;
  }

  if (systemPromptsError) {
    return <ApiErrorMessage apiError={systemPromptsError} />;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Card sx={{ mb: 1 }} elevation={0}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            AI Prompts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage client-level AI prompt overrides. Prompts marked as &quot;Override&quot; use custom values for this
            client. Prompts marked as &quot;Default&quot; use the system-level settings.
          </Typography>
        </CardContent>
      </Card>

      <ClientAiPromptsTable
        prompts={promptItems}
        onEditClick={handleEditClick}
        onResetClick={handleResetPrompt}
        isUpdating={isUpdating}
        updateError={updateError}
        canEdit={isSuperUser && !isReadOnly}
      />

      {editDialogOpen && selectedPrompt && (
        <EditClientAiPromptDialog
          prompt={selectedPrompt}
          onClose={closeEditDialog}
          onSave={handleSavePrompt}
          isLoading={isUpdating}
        />
      )}

      {updateError && <SnackbarApiError error={updateError} />}
      {updateSuccess && <SnackbarAlert message="AI prompt updated successfully" severity="success" />}
    </Box>
  );
};

export { AiPromptsTab };
